import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { yupResolver } from '@hookform/resolvers/yup';
import { ClientError, APIError, useErrorHandler } from 'shared-ui';

import { AddEventExportFormatBody } from 'api/eventsExportFormat/eventsExportFormats.types';
import {
  addEventExportFormat,
  fetchEventsExportFormatConfig,
  fetchEventExportFormatDetails,
  previewEventExportFormat,
  editEventExportFormat,
} from 'api/eventsExportFormat/eventsExportFormats';
import { getAutoExportPreview } from 'api/autoExport/autoExport';
import { FormatTyp } from 'api/autoExport/autoExport.enum';
import { useMutation } from 'hooks/useMutation/useMutation';
import { QueryKeyEnum } from 'core/global.enum';
import { useQuery } from 'hooks/useQuery/useQuery';
import { useLocale } from 'hooks/useLocale/useLocale';
import { usePermissions } from 'hooks/usePermissions/usePermissions';
import { AppRoute } from 'routing/AppRoute.enum';
import { AppMessages } from 'i18n/messages';
import {
  isSystemFormatTyp,
  SYSTEM_EVENT_EXPORT_FORMAT_CONFIGS,
  SYSTEM_FORMAT_EXTRA_DISPLAY_ROWS,
  SYSTEM_FORMAT_PARAMETER_LABEL_OVERRIDES,
  SYSTEM_FORMAT_TOP_DISPLAY_ROWS,
} from '../EventsExportFormats.systemFormats';

import { AddEventExportFormat } from './AddEventExportFormat';
import { useAddEventExportFormatValidation } from './AddEventExportFormat.validation';
import { AddEventExportFormatPageModeEnum } from './AddEventExportFormat.enum';
import { addEventExportFormatDefaultFormValues, AddEventExportFormatUrlParams } from './AddEventExportFormat.types';

export const AddEventExportFormatContainer = () => {
  const { formatMessage } = useLocale();
  const { id: exportFormatId, mode } = useParams<AddEventExportFormatUrlParams>();
  const navigate = useNavigate();
  const { handleError } = useErrorHandler();
  const { perms } = usePermissions();
  const canViewDefinicje = !!perms?.DEFINICJE?.view;
  const isAddPageMode = mode === AddEventExportFormatPageModeEnum.ADD;
  const isEditPageMode = !!exportFormatId && mode === AddEventExportFormatPageModeEnum.EDIT;
  const isPreviewPageMode = !!exportFormatId && mode === AddEventExportFormatPageModeEnum.PREVIEW;
  const isSystemFormatPreview = isPreviewPageMode && isSystemFormatTyp(exportFormatId);
  const systemFormatConfig = isSystemFormatPreview ? SYSTEM_EVENT_EXPORT_FORMAT_CONFIGS[exportFormatId] : undefined;

  const { schema } = useAddEventExportFormatValidation();
  const formMethods = useForm<AddEventExportFormatBody>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: addEventExportFormatDefaultFormValues,
  });

  const {
    data: eventExportFormat,
    isInitialLoading: isLoadingEventExportFormat,
    isRefetching: isRefetchingEventExportFormat,
  } = useQuery([QueryKeyEnum.EVENT_EXPORT_FORMAT_DETAILS], () => fetchEventExportFormatDetails(exportFormatId), {
    enabled: (isEditPageMode || isPreviewPageMode) && !isSystemFormatPreview,
    refetchOnMount: true,
  });

  const { data: eventsExportFormatConfig, isLoading: isLoadingEventExportFormatConfig } = useQuery(
    [QueryKeyEnum.EVENTS_EXPORT_FORMAT_CONFIG],
    fetchEventsExportFormatConfig,
  );

  const { data: systemFormatPreview, isInitialLoading: isLoadingSystemFormatPreview } = useQuery(
    [QueryKeyEnum.AUTO_EXPORT_PREVIEW, exportFormatId],
    () => getAutoExportPreview(exportFormatId as FormatTyp),
    {
      enabled: isSystemFormatPreview && canViewDefinicje,
    },
  );

  const { mutate: onAddEventExportFormat, isLoading: isLoadingAddExportFormat } = useMutation(addEventExportFormat, {
    onSuccess: () => {
      navigate(AppRoute.eventsExportFormats);
      toast.dark(formatMessage({ id: AppMessages['definitions.events.addExportFormat.addSuccessMessage'] }));
    },
    onError: (error: ClientError<APIError>) => {
      const errorFields = error.response?.data.fields;
      const isNameExistsError = errorFields?.find(({ type, field }) => type === 'exists' && field === 'nazwa');

      if (isNameExistsError) {
        return formMethods.setError('nazwa', {
          message: formatMessage({ id: AppMessages['definitions.events.addExportFormat.nameExists'] }),
        });
      }
      handleError({ error, omitAPIErrorMessage: true });
    },
  });

  const { mutate: onEditEventExportFormat, isLoading: isLoadingEditExportFormat } = useMutation(editEventExportFormat, {
    onSuccess: () => {
      navigate(AppRoute.eventsExportFormats);
      toast.dark(formatMessage({ id: AppMessages['definitions.events.addExportFormat.editSuccessMessage'] }));
    },
    onError: (error: ClientError<APIError>) => {
      const errorFields = error.response?.data.fields;
      const isNameExistsError = errorFields?.find(({ type, field }) => type === 'exists' && field === 'nazwa');

      if (isNameExistsError) {
        return formMethods.setError('nazwa', {
          message: formatMessage({ id: AppMessages['definitions.events.addExportFormat.nameExists'] }),
        });
      }
      handleError({ error, omitAPIErrorMessage: true });
    },
  });

  const {
    mutate: onPreviewEventExportFormat,
    data: eventExportFormatPreview,
    isLoading: isLoadingPreviewEventExportFormat,
  } = useMutation(previewEventExportFormat);

  const onSubmit = (body: AddEventExportFormatBody) => {
    if (isEditPageMode) {
      onEditEventExportFormat({
        id: exportFormatId,
        body,
      });
    } else {
      onAddEventExportFormat(body);
    }
  };

  const onResetForm = () => {
    if (systemFormatConfig) {
      formMethods.reset(systemFormatConfig);
      return;
    }

    if (eventExportFormat && (isEditPageMode || isPreviewPageMode)) {
      const { id, createdAt, updatedAt, ...eventExportFormatFormatValues } = eventExportFormat;

      formMethods.reset(eventExportFormatFormatValues);
      onPreviewEventExportFormat(eventExportFormatFormatValues);
    } else if (eventsExportFormatConfig && isAddPageMode) {
      const { date, endline, separator } = eventsExportFormatConfig;

      const defaultFormValues = {
        ...addEventExportFormatDefaultFormValues,
        endline: endline[0],
        separator: separator[0],
        data: { ...addEventExportFormatDefaultFormValues.data, format: date[0] },
      };

      formMethods.reset(defaultFormValues);
      onPreviewEventExportFormat(defaultFormValues);
    }
  };

  useEffect(() => {
    onResetForm();
  }, [eventExportFormat, eventsExportFormatConfig, systemFormatConfig]);

  const previewText = isSystemFormatPreview ? systemFormatPreview?.sampleContent : eventExportFormatPreview?.preview;

  return (
    <FormProvider {...formMethods}>
      <AddEventExportFormat
        onSubmit={onSubmit}
        isLoading={isLoadingEventExportFormat || isLoadingEventExportFormatConfig || isRefetchingEventExportFormat}
        isLoadingAddExportFormat={isLoadingAddExportFormat || isLoadingEditExportFormat}
        eventsExportFormatConfig={eventsExportFormatConfig}
        eventExportFormatPreview={previewText}
        isLoadingPreviewEventExportFormat={isLoadingPreviewEventExportFormat || isLoadingSystemFormatPreview}
        onPreviewEventExportFormat={onPreviewEventExportFormat}
        isPreviewPageMode={isPreviewPageMode}
        isEditPageMode={isEditPageMode}
        parameterLabelOverrides={
          isSystemFormatPreview ? SYSTEM_FORMAT_PARAMETER_LABEL_OVERRIDES[exportFormatId] : undefined
        }
        extraDisplayRowLabelKeys={isSystemFormatPreview ? SYSTEM_FORMAT_EXTRA_DISPLAY_ROWS[exportFormatId] : undefined}
        topDisplayRowLabelKeys={isSystemFormatPreview ? SYSTEM_FORMAT_TOP_DISPLAY_ROWS[exportFormatId] : undefined}
        showCzytnikSign={isSystemFormatPreview}
      />
    </FormProvider>
  );
};
